import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  EXTRAS_MENU_TOOLTIP_PROPS,
  ExportMenuItems,
  ExposureTypeClientEnum,
  FormControlSizes,
  IconButton,
  IconName,
  MixpanelEvent,
  Toggle,
  baseTreeGroupColumnDef,
  filterExistsAndExcludes,
  removeEmptyFilters,
  stringColumnComparatorMultiColumn,
  useAccordionFilterBuilder,
  useAggDeltaUpdatesPipe,
  useBlotterTable,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useMixpanel,
  usePersistedBlotterTable,
  useWSFilterPipe,
  type CreditBlotterExposure,
  type FilterClause,
  type FilterableProperty,
  type Leaves,
  type MinimalSubscriptionResponse,
} from '@talos/kyoko';
import type { ColDef, GridOptions, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { isEqual } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { map, pipe } from 'rxjs';
import { CONTROL_TOOLTIPS } from '../tooltips';
import { defaultCreditFilter } from './defaults';
import type { CreditTabProps, CreditTableFilter } from './types';
import { useCreditBlotterDataObs } from './useCreditBlotterDataObs';
import { useCreditColumns } from './useCreditColumns';
import { useCreditFilter } from './useCreditFilter';
import { useCreditMenu } from './useCreditMenu';

const creditBlotterExposureComparator = stringColumnComparatorMultiColumn<CreditBlotterExposure>([
  'MarketAccount',
  'Currency',
  'ExposureDefinition',
]);

function getCreditBlotterExposureKey(cbe: CreditBlotterExposure): string {
  return cbe.rowID;
}

function getDataPath(item: CreditBlotterExposure) {
  return item.treePath;
}

const AGG_SPECS = [
  {
    valuePath: 'effectiveExposureWithSign',
    currencyPath: 'ExposureCurrency',
  },
  {
    valuePath: 'effectiveRemainingCredit',
    currencyPath: 'ExposureCurrency',
  },
  {
    valuePath: 'effectiveLimitWithSign',
    currencyPath: 'ExposureCurrency',
  },
  {
    valuePath: 'effectiveSide.talosExposure',
    currencyPath: 'ExposureCurrency',
  },
  {
    valuePath: 'effectiveSide.talosExposureLimit',
    currencyPath: 'ExposureCurrency',
  },
] satisfies {
  valuePath: Leaves<CreditBlotterExposure>;
  currencyPath: Leaves<CreditBlotterExposure>;
}[];

export interface ExposuresBlotterParams {
  blotterID: string;
  tab: CreditTabProps;
  onUpdateTab: (tab: CreditTabProps) => void;
  initialIsOpen?: boolean;
  onCloneTab: () => void;
}

export function CreditBlotter({ blotterID, tab, onUpdateTab, onCloneTab, initialIsOpen }: ExposuresBlotterParams) {
  const mixpanel = useMixpanel();

  const defaultColumns = useCreditColumns(tab.defaultColumns);
  const defaultFilter = tab.defaultFilter ?? defaultCreditFilter;
  const showZeroBalances = tab.showZeroBalances ?? true; // default to true if undefined

  // defaults to false, and only have it be true if the config is enabled too
  const showTotals = tab.showTotals ?? false;

  const { dataObservable, errorObservable } = useCreditBlotterDataObs({
    showZeroBalances: tab.showZeroBalances,
    tag: 'Credit blotter',
  });

  const persistedBlotterTable = usePersistedBlotterTable(blotterID, { filter: defaultFilter });

  const filterResults = useCreditFilter({
    persistedBlotterTable,
  });

  const { changeFilter, filterableProperties, initialFilterClauses, filter } = filterResults;
  const handleFilterClausesChanged = useCallback(
    (filterClausesByPropertyKey: Map<string, FilterClause>, propertiesByKey: Map<string, FilterableProperty>) => {
      changeFilter(curr => {
        const newFilter = removeEmptyFilters<CreditTableFilter>({
          ...curr,
          ...(Object.fromEntries(
            [...propertiesByKey.keys()].map(key => [key, filterClausesByPropertyKey.get(key)?.selections])
          ) as unknown as CreditTableFilter),
        });
        if (isEqual(curr, newFilter)) {
          return curr;
        }
        return newFilter;
      });
    },
    [changeFilter]
  );
  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps: {
      initialFilterClauses,
      properties: filterableProperties,
      onFilterClausesChanged: handleFilterClausesChanged,
    },
  });

  const creditMenu = useCreditMenu({
    openClause: filterBuilderAccordion.openClause,
    filterableProperties: filterableProperties,
  });

  const columnsWithMenu = useMemo(() => [...defaultColumns, creditMenu.column], [creditMenu.column, defaultColumns]);

  const rxjsFilterFunc = useCallback(
    (data: CreditBlotterExposure) => {
      if (filterExistsAndExcludes(filter, 'Accounts', data, 'MarketAccount')) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'Currencies', data, 'Currency')) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'Types', data, 'type')) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'Statuses', data, 'status')) {
        return false;
      }
      return true;
    },
    [filter]
  );
  const filterPipe = useWSFilterPipe({ getUniqueKey: getCreditBlotterExposureKey, filterFunc: rxjsFilterFunc });
  const filteredDataObservable = useMemo(() => dataObservable.pipe(filterPipe), [filterPipe, dataObservable]);

  const autoGroupColumnDef: ColDef = useMemo(
    () =>
      ({
        ...baseTreeGroupColumnDef,
        valueFormatter: ({ data, context, node }: ValueFormatterParams<CreditBlotterExposure>) => {
          const marketAccountName = data?.MarketAccount ?? node?.key;
          if (!marketAccountName) {
            return 'Unknown';
          }

          const marketAccount = context.current.marketAccountsByName?.get(marketAccountName);
          return marketAccount?.DisplayName ?? marketAccount?.Name ?? '';
        },
        // This filterValueGetter is used to get the filtering context menu item in the right-click menu
        filterValueGetter: ({ data }: ValueGetterParams<CreditBlotterExposure>) => {
          return data?.MarketAccount;
        },
        headerName: 'Market Account',
        initialSort: 'asc',
        comparator: (valueA, valueB, nodeA, nodeB) => creditBlotterExposureComparator(nodeA.data, nodeB.data),
      }) satisfies GridOptions['autoGroupColumnDef'],
    []
  );

  const aggDeltaUpdatesPipe = useAggDeltaUpdatesPipe({
    getUniqueKey: getCreditBlotterExposureKey,
    aggSpecs: AGG_SPECS,
  });
  const totalsPipe = useMemo(() => {
    return pipe(
      map((json: MinimalSubscriptionResponse<CreditBlotterExposure>) => {
        // Only do totals aggregation on Exposures (not MarketExposures)
        return { ...json, data: json.data.filter(cbe => cbe.type === ExposureTypeClientEnum.Exposure) };
      }),
      aggDeltaUpdatesPipe
    );
  }, [aggDeltaUpdatesPipe]);

  const blotterTable = useBlotterTable<CreditBlotterExposure>({
    dataObservable: filteredDataObservable,
    errorObservable,
    pinnedRowDataPipe: totalsPipe,
    rowID: 'rowID' satisfies keyof CreditBlotterExposure,
    persistence: persistedBlotterTable,
    showPinnedRows: showTotals,
    columns: columnsWithMenu,
    gridOptions: {
      showOpenedGroup: true,
      autoGroupColumnDef,
      treeData: true,
      getDataPath,
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      getContextMenuItems: creditMenu.getContextMenuItems,
    },
    exportOptions: {
      baseParams: { skipRowGroups: false },
      name: 'Credit',
      tabLabel: tab.label,
    },
  });

  const { expandAllGroups, collapseAllGroups } = blotterTable;

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const handleCloneTab = useCallbackRef(() => {
    onCloneTab?.();
    extrasMenuPopover.close();
  });

  const updateShowZeroBalances = useCallbackRef((value: boolean) => {
    mixpanel.track(MixpanelEvent.ToggleShowZeroBalances);
    onUpdateTab({ ...tab, showZeroBalances: value });
  });

  const updateShowTotals = useCallbackRef((value: boolean) => {
    mixpanel.track(MixpanelEvent.ToggleShowTotals);
    onUpdateTab({ ...tab, showTotals: value });
  });

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTable.blotterTableFiltersProps}
        prefix={
          <>
            <IconButton
              icon={IconName.ListExpand}
              size={FormControlSizes.Small}
              variant={ButtonVariants.Default}
              onClick={expandAllGroups}
              data-testid="expand-all-button"
            />
            <IconButton
              icon={IconName.ListCollapse}
              size={FormControlSizes.Small}
              variant={ButtonVariants.Default}
              onClick={collapseAllGroups}
            />
          </>
        }
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            <Toggle
              tooltip={CONTROL_TOOLTIPS.showZeroBalances}
              tooltipProps={EXTRAS_MENU_TOOLTIP_PROPS}
              size={FormControlSizes.Small}
              checked={showZeroBalances}
              onChange={updateShowZeroBalances}
              label="Show Zero Balances"
            />
            <Toggle
              size={FormControlSizes.Small}
              checked={showTotals}
              onChange={updateShowTotals}
              label="Show Totals"
            />

            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
            <Button
              startIcon={IconName.Duplicate}
              variant={ButtonVariants.Default}
              size={FormControlSizes.Small}
              onClick={handleCloneTab}
            >
              Clone Tab
            </Button>
          </BlotterTableExtrasMenu>
        }
      />
      <BlotterTable {...blotterTable} />
      {creditMenu.dialogs}
    </>
  );
}
